import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '../components/Card';
import {
  ChevronDown, ChevronRight, Users, Shield, Crown, Swords, Camera,
  Trophy, Building2, UserCheck, Zap, MessageSquare, BarChart3,
  AlertCircle, Globe, Bot, CreditCard, FileText,
} from 'lucide-react';
import { api, type Me } from '../lib/api';
import { useEffectiveRole } from '../lib/viewAs';
import { SETTLE, MORPH, settleDelay } from '../lib/motion';

function Collapsible({ title, icon: Icon, children, defaultOpen = false }: {
  title: string; icon: typeof Users; children: React.ReactNode; defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-lg bg-white/[0.04] overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className={`${MORPH} flex w-full items-center gap-3 p-4 text-left transition-colors hover:bg-white/[0.06]`}
      >
        <Icon className="h-5 w-5 text-ink-secondary shrink-0" />
        <span className="flex-1 font-display text-sm font-800 uppercase tracking-wider text-ink-primary">{title}</span>
        {open ? <ChevronDown className="h-4 w-4 text-ink-meta" /> : <ChevronRight className="h-4 w-4 text-ink-meta" />}
      </button>
      {open && <div className="px-4 pb-4 pt-0 text-sm leading-relaxed space-y-3 text-ink-body">{children}</div>}
    </div>
  );
}

function Step({ number, title, children }: { number: number; title: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-4">
      <div className="shrink-0 flex h-8 w-8 items-center justify-center rounded-lg bg-white/[0.05] font-display text-sm font-900 text-ink-secondary">
        {number}
      </div>
      <div className="flex-1">
        <p className="font-bold mb-1 text-ink-primary">{title}</p>
        <div className="text-sm text-ink-secondary">{children}</div>
      </div>
    </div>
  );
}

export function HowItWorks() {
  const { data: me } = useQuery<Me>({ queryKey: ['me'], queryFn: api.me });
  const role = useEffectiveRole(me?.role);
  const tagline = role === 'founder'
    ? "Founder reference: the full operating model. Switch ‘view as’ bottom-left to read this from a member, leader or admin perspective."
    : role === 'admin'
    ? "Admin reference: how the platform works end-to-end. Pay attention to the Run-It section — that’s your toolkit."
    : role === 'leader'
    ? "Leader reference: how clans, matches and screenshots flow through the system. Focus on the Match Day section."
    : "Everything you need to know about competing on GG Anvil — from getting your portal link to submitting screenshots.";

  return (
    <div className="mx-auto max-w-4xl space-y-8 pb-16">
      <div className={SETTLE} style={settleDelay(0)}>
        <h1 className="font-display text-3xl font-900 uppercase tracking-tight text-ink-primary">How GG Anvil Works</h1>
        <p className="mt-2 text-sm text-ink-secondary">{tagline}</p>
      </div>

      {/* ═══ GETTING STARTED ═══ */}
      <div className={SETTLE} style={settleDelay(1)}>
      <Card>
        <h2 className="font-display text-lg font-800 uppercase tracking-wider text-ink-primary mb-4">Getting Started</h2>
        <div className="space-y-4">
          <Step number={1} title="Get Your Portal Link">
            <p>Type <code className="rounded bg-iron-800 px-1.5 py-0.5 font-mono text-xs">/ggportal</code> in any Discord channel where the GG Anvil bot is active. You'll get a personal, secure link to the portal to submit scores and view team standings. Don't share it, anyone with your link has your access.</p>
          </Step>
          <Step number={2} title="Set Your Gamertag">
            <p>First time in? Head to <strong>My Card</strong> and set your CoD ID (gamertag with the # number, e.g. <code className="rounded bg-iron-800 px-1.5 py-0.5 font-mono text-xs">YourName#1234</code>). This is how Anvil matches you to scoreboards.</p>
          </Step>
          <Step number={3} title="Join or Create a Clan">
            <p>If your clan leader has already set things up, your seat will be waiting. If not, leaders can create clans and invite members through Discord commands or the portal.</p>
          </Step>
        </div>
      </Card>
      </div>

      {/* ═══ THE ROLE SYSTEM ═══ */}
      <div className={SETTLE} style={settleDelay(2)}>
      <Card>
        <h2 className="font-display text-lg font-800 uppercase tracking-wider text-ink-primary mb-4">The Role System</h2>
        <p className="text-sm mb-4 opacity-80">Everyone has a role that sets what they can see and do. Roles are layered — <strong>platform</strong>, <strong>org</strong>, <strong>competition</strong>, and <strong>clan</strong> — each scoped to what it controls.</p>

        <div className="space-y-3">
          <Collapsible title="Founder" icon={Shield}>
            <p><strong>Founder</strong> — the people who built and run GG Anvil. Full access to everything, every org, every competition. Can appoint tiers, manage the asset library, and onboard new organisations.</p>
          </Collapsible>

          <Collapsible title="Org Owner, Admin, Member & Viewer" icon={Building2}>
            <p><strong>Org Owners</strong> run an organisation — billing, members, competitions, branding. <strong>Org Admins</strong> help run it: create and manage comps, handle disputes, override scores, and manage clans, but can't touch billing or delete the org.</p>
            <p className="mt-2"><strong>Org Members</strong> can view their org's competitions and request new ones. <strong>Org Viewers</strong> are read-only.</p>
            <p className="mt-2 text-xs text-ink-meta">Org Admin is a separate role from the retired platform Admin tier — it's alive and well, scoped to one org.</p>
          </Collapsible>

          <Collapsible title="Competition Roles" icon={Trophy}>
            <p><strong>Comp Owners</strong> have full control over a specific competition — format, scheduling, results. Usually the org owner or a designated admin.</p>
            <p><strong>Comp Admins</strong> manage day-to-day: resolving disputes, overriding scores, managing brackets. They can't delete the competition.</p>
            <p><strong>Comp Members</strong> are the players and leaders who participate. They can view standings, submit scores, and track matches.</p>
          </Collapsible>

          <Collapsible title="Clan Roles" icon={Crown}>
            <p>Clan-scoped roles are displayed as <strong>Leader</strong>, <strong>Co-Admin</strong>, and <strong>Member</strong>:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong>Leader</strong> — runs the clan and roster: sets up the roster, approves seat claims, schedules matches, and agrees to (or declines) proposed matches</li>
              <li><strong>Co-Admin</strong> — helps the leader run the clan</li>
              <li><strong>Member</strong> — plays</li>
            </ul>
          </Collapsible>

          <Collapsible title="Players (Members)" icon={Users}>
            <p>The core of the platform. As a player you can:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Claim your seat in a clan (match your gamertag to the roster)</li>
              <li>View live standings, match history, and your personal stats</li>
              <li>Submit scoreboard screenshots after matches</li>
              <li>View your player card with KD, matches played, and more</li>
              <li>Set your avatar, banner, and card colour for a personalised player card</li>
            </ul>
          </Collapsible>
        </div>
      </Card>
      </div>

      {/* ═══ HOW COMPETITIONS WORK ═══ */}
      <div className={SETTLE} style={settleDelay(3)}>
      <Card>
        <h2 className="font-display text-lg font-800 uppercase tracking-wider text-ink-primary mb-4">How Competitions Work</h2>

        <div className="space-y-3">
          <Collapsible title="Competition Stages" icon={BarChart3}>
            <p>Every competition runs a <strong>stage pipeline</strong> — an ordered sequence of stages the organiser composes in Setup. A comp isn't locked to one fixed shape; organisers pick and order the stages that fit their format. One stage is live at a time, and stages finish in order.</p>
            <div className="space-y-3 mt-2">
              <div className="rounded-lg bg-white/[0.04] p-3">
                <p className="font-bold text-ink-primary">Seeding</p>
                <p>Teams play initial rounds to establish skill level; feeds division placement.</p>
              </div>
              <div className="rounded-lg bg-white/[0.04] p-3">
                <p className="font-bold text-ink-primary">Round Robin</p>
                <p>Every team plays every other team.</p>
              </div>
              <div className="rounded-lg bg-white/[0.04] p-3">
                <p className="font-bold text-ink-primary">Divisions <span className="font-normal text-ink-meta">(formerly Groups)</span></p>
                <p>Teams split into divisions (from seeding standings), each running its own mini round-robin.</p>
              </div>
              <div className="rounded-lg bg-white/[0.04] p-3">
                <p className="font-bold text-ink-primary">Swiss</p>
                <p>Teams are paired each round by similar records, not a fixed bracket.</p>
              </div>
              <div className="rounded-lg bg-white/[0.04] p-3">
                <p className="font-bold text-ink-primary">Elimination (Single or Double)</p>
                <p>Bracket-style knockout.</p>
              </div>
            </div>
            <p className="mt-3">e.g. Seeding &rarr; Divisions &rarr; Single Elimination, or Seeding &rarr; Swiss &rarr; Round Robin. Each stage can carry its own series format (Bo3/Bo5/Bo7) and game-mode rules.</p>
          </Collapsible>

          <Collapsible title="Game Modes" icon={Swords}>
            <p>Game modes are configured per competition by the org admin. Common modes include Hardpoint, Search & Destroy, Overload, and Control — but any combination is supported.</p>
            <p className="mt-2">Each mode has its own score limit and seeding point value. The competition's series format (Bo3, Bo5, Bo7) determines how many games are played and in what order.</p>
            <p className="mt-2">The team that wins the majority of games wins the match night.</p>
          </Collapsible>

          <Collapsible title="Rules & Code of Conduct" icon={FileText}>
            <p>Every competition starts with a default <strong>Rules & Code of Conduct</strong> (fair play, match conduct, disputes, consequences) — organisers edit it in Setup to add game-specific details.</p>
            <p className="mt-2">Before a clan leader can self-register their clan into a comp, they must <strong>accept its current rules</strong>. Acceptance is durable — once a clan accepts, that stands for the life of the comp even if the org edits the rules later.</p>
            <p className="mt-2">Every comp also has a <strong>shareable public rules page</strong> anyone can open — no login needed.</p>
          </Collapsible>
        </div>
      </Card>
      </div>

      {/* ═══ MATCH NIGHT FLOW ═══ */}
      <div className={SETTLE} style={settleDelay(4)}>
      <Card>
        <h2 className="font-display text-lg font-800 uppercase tracking-wider text-ink-primary mb-4">Match Night — Step by Step</h2>

        <div className="space-y-4">
          <Step number={1} title="Propose or Schedule the Match">
            <p>A clan leader proposes a match against an opponent (date/time). Since a leader can't commit another team, it starts <strong>awaiting agreement</strong> — not live yet. The opponent's leader gets a DM and must <strong>Agree</strong> or <strong>Decline</strong>.</p>
            <p className="mt-2">An admin- or founder-created match is authoritative immediately (no agreement step). Declining soft-cancels the proposal (nothing deleted) and DMs the proposer.</p>
          </Step>

          <Step number={2} title="Match Goes Live">
            <p>Once agreed (or admin-created), both leaders get a match-card DM from the Anvil bot with the opponent, modes, and a link to the portal. The match status changes to <strong>IN PROGRESS</strong>.</p>
          </Step>

          <Step number={3} title="Play Your Games">
            <p>Go play your S&D, Hardpoint, and Overload matches in-game. Take a screenshot of the scoreboard after each game mode.</p>
          </Step>

          <Step number={4} title="Submit Screenshots">
            <p>Open your portal link. If you owe screenshots, Anvil takes over your entire screen — no sneaking off to look at standings. You'll see a big "Submit Your Screenshot" prompt with the modes you still need to submit.</p>
            <p className="mt-2">Upload your scoreboard screenshot for each game mode. Each screenshot the AI reads costs <strong>1 credit</strong> — one image, one read. That's the whole unit.</p>
          </Step>

          <Step number={5} title="AI Reads the Scoreboard">
            <p>Anvil's OCR pipeline kicks in. It checks if the image is actually a scoreboard (not a random photo), preprocesses it, extracts scores and player stats, validates the data against known players, and posts the results. All in about 3 seconds.</p>
          </Step>

          <Step number={6} title="Results Are Confirmed">
            <p>Anvil cross-references both teams' submissions to reach consensus on the scores — there's no manual leader sign-off. If the two sides' reads don't line up, an admin steps in to resolve the dispute.</p>
            <p className="mt-2">Once consensus is reached, the match is <strong>CONFIRMED</strong> and standings update immediately.</p>
          </Step>

          <Step number={7} title="Standings Update">
            <p>Confirmed results feed into the standings table. KD ratios, win rates, and brag scores all update in real time. Check the Standings page to see where your clan sits.</p>
          </Step>
        </div>
      </Card>
      </div>

      {/* ═══ THE SUBMISSION TAKEOVER ═══ */}
      <div className={SETTLE} style={settleDelay(5)}>
      <Card>
        <h2 className="font-display text-lg font-800 uppercase tracking-wider text-ink-primary mb-4">The Submission Takeover</h2>
        <div className="flex items-start gap-4">
          <Camera className="h-8 w-8 text-ink-secondary shrink-0 mt-1" />
          <div>
            <p>If you have outstanding screenshots to submit, Anvil won't let you do anything else in the portal. No browsing standings, no checking stats — just a full-screen prompt to submit your evidence.</p>
            <p className="mt-2">This isn't a bug, it's a feature. It keeps match nights moving and ensures results come in on time. Once you've submitted all your screenshots, the portal unlocks and you can browse freely.</p>
            <p className="mt-2">There's a submission deadline (usually 24 hours after the match ends). Miss it and your admin will need to handle it manually.</p>
          </div>
        </div>
      </Card>
      </div>

      {/* ═══ CLANS & SEATS ═══ */}
      <div className={SETTLE} style={settleDelay(6)}>
      <Card>
        <h2 className="font-display text-lg font-800 uppercase tracking-wider text-ink-primary mb-4">Clans & the Seat System</h2>

        <div className="space-y-3">
          <Collapsible title="What Are Seats?" icon={UserCheck}>
            <p>Every clan has a roster of <strong>seats</strong> — slots for each player. Seats start as <strong>ghosts</strong> (just a gamertag with no linked Discord account). When a real player claims a seat by matching their gamertag, it becomes <strong>claimed</strong>.</p>
            <p className="mt-2">This system lets org admins set up full rosters from screenshots before players even join the platform. When players arrive, they just claim their seat.</p>
          </Collapsible>

          <Collapsible title="Claiming Your Seat" icon={Users}>
            <p>When you first join, find your clan and look for your gamertag in the roster. Click "Claim" and your clan leader will approve it. Once approved, you're linked to that seat and your stats start tracking.</p>
            <p className="mt-2">If your gamertag doesn't match exactly (including the #number), you won't find your seat. Make sure your CoD ID is set correctly in your profile.</p>
          </Collapsible>

          <Collapsible title="Leadership Claims" icon={Crown}>
            <p>If a clan doesn't have a leader yet, you can submit a leadership claim. The org admin reviews and approves or rejects it. Only one leader per clan.</p>
          </Collapsible>
        </div>
      </Card>
      </div>

      {/* ═══ DISCORD INTEGRATION ═══ */}
      <div className={SETTLE} style={settleDelay(7)}>
      <Card>
        <h2 className="font-display text-lg font-800 uppercase tracking-wider text-ink-primary mb-4">Discord Integration</h2>

        <div className="space-y-3">
          <Collapsible title="Bot Commands" icon={Bot}>
            <p>GG Anvil lives in your Discord server through two slash commands:</p>
            <div className="mt-2 space-y-2">
              <div className="rounded-lg bg-white/[0.04] p-2.5">
                <code className="font-mono text-ink-body text-xs">/ggportal</code>
                <p className="text-xs mt-1 text-ink-secondary">Your personal, secure portal link (valid ~2 hours). Your main entry point for everything — profile, clan, matches, standings.</p>
              </div>
              <div className="rounded-lg bg-white/[0.04] p-2.5">
                <code className="font-mono text-ink-body text-xs">/ggveto</code>
                <p className="text-xs mt-1 text-ink-secondary">Start a co-branded map-veto session with another server member (where enabled).</p>
              </div>
            </div>
            <p className="mt-2">Most day-to-day actions — creating a clan, updating your profile, checking match status — now happen inside the portal (via <code className="rounded bg-iron-800 px-1.5 py-0.5 font-mono text-xs">/ggportal</code>) rather than as separate commands.</p>
          </Collapsible>

          <Collapsible title="Match Notifications" icon={MessageSquare}>
            <p>When a match is agreed (or admin-created), both clan leaders get a DM from the bot with:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>The opponent clan name</li>
              <li>Game modes for the night</li>
              <li>A direct link to submit results</li>
              <li>The submission deadline</li>
            </ul>
            <p className="mt-2">Results are also posted to your server's designated channel when matches are confirmed.</p>
          </Collapsible>
        </div>
      </Card>
      </div>

      {/* ═══ AI SCORE CAPTURE ═══ */}
      <div className={SETTLE} style={settleDelay(8)}>
      <Card>
        <h2 className="font-display text-lg font-800 uppercase tracking-wider text-ink-primary mb-4">AI Score Capture (OCR)</h2>

        <div className="space-y-3">
          <Collapsible title="How It Works" icon={Zap}>
            <p>Upload a screenshot. Anvil's AI reads it, extracts every score, and posts the results — in about 3 seconds.</p>
            <div className="space-y-2 mt-2">
              <div className="flex items-start gap-3">
                <span className="shrink-0 font-display text-xs font-900 text-ink-secondary bg-white/[0.05] rounded px-2 py-0.5">1</span>
                <p><strong>Image Verification</strong> — AI checks the upload is a real scoreboard, not a selfie, menu screen, or meme</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="shrink-0 font-display text-xs font-900 text-ink-secondary bg-white/[0.05] rounded px-2 py-0.5">2</span>
                <p><strong>Score Extraction</strong> — AI vision reads team scores, individual stats, and player names from the scoreboard</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="shrink-0 font-display text-xs font-900 text-ink-secondary bg-white/[0.05] rounded px-2 py-0.5">3</span>
                <p><strong>Roster Matching</strong> — extracted names are fuzzy-matched against the registered roster to correct reading errors</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="shrink-0 font-display text-xs font-900 text-ink-secondary bg-white/[0.05] rounded px-2 py-0.5">4</span>
                <p><strong>Validation & Anti-Cheat</strong> — scores checked for plausibility, player identity verified, cross-checked against opposing team's submission</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="shrink-0 font-display text-xs font-900 text-ink-secondary bg-white/[0.05] rounded px-2 py-0.5">5</span>
                <p><strong>Results Posted</strong> — verified scores posted to the match, player stats and career records updated</p>
              </div>
            </div>
          </Collapsible>

          <Collapsible title="Credits & Billing" icon={CreditCard}>
            <p>Simple, per-read pricing: <strong>one screenshot the AI reads = 1 credit = 1&cent;</strong>. No tiers, no per-player fees — Anvil only charges when the OCR actually reads an image, and a duplicate submission is locked before it costs anything.</p>
            <p className="mt-3">Credits are bought in bundles and managed at the org level:</p>
            <div className="grid grid-cols-3 gap-3 mt-2">
              <div className="rounded-lg bg-white/[0.04] p-3 text-center">
                <p className="font-display text-xl font-900 text-ink-primary">$10</p>
                <p className="text-[10px] text-ink-meta mt-1">1,000 credits</p>
              </div>
              <div className="rounded-lg bg-white/[0.04] p-3 text-center">
                <p className="font-display text-xl font-900 text-ink-primary">$45</p>
                <p className="text-[10px] text-ink-meta mt-1">5,000 · save 10%</p>
              </div>
              <div className="rounded-lg bg-white/[0.04] p-3 text-center">
                <p className="font-display text-xl font-900 text-ink-primary">$200</p>
                <p className="text-[10px] text-ink-meta mt-1">25,000 · save 20%</p>
              </div>
            </div>
            <p className="mt-3">Credits never expire, and there's a flat <strong>$5/month per organisation</strong> data fee on top — that's the whole bill.</p>
            <p className="mt-3"><strong>Series length drives cost.</strong> Every game in a series has its own screenshots to read, so longer series use more credits per match: a <strong>Bo5 runs about 1.6× the credits of a Bo3</strong>, and a <strong>Bo7 over 2×</strong>. Pick series lengths per stage with that in mind — e.g. Bo3 seeding, Bo5 finals.</p>
          </Collapsible>

          <Collapsible title="Tips for Good Screenshots" icon={Camera}>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Use a direct screenshot (Share button / Screenshot key), not a photo of your screen</li>
              <li>Make sure the full scoreboard is visible — don't crop out player names</li>
              <li>Avoid screenshots with overlays, notifications, or other UI elements on top</li>
              <li>If using a phone photo, keep it straight-on with minimal glare</li>
              <li>PNG or JPG, max 10MB</li>
            </ul>
          </Collapsible>
        </div>
      </Card>
      </div>

      {/* ═══ PLAYER STATS & CARDS ═══ */}
      <div className={SETTLE} style={settleDelay(9)}>
      <Card>
        <h2 className="font-display text-lg font-800 uppercase tracking-wider text-ink-primary mb-4">Player Stats & Cards</h2>
        <div className="space-y-3">
          <Collapsible title="What's Tracked" icon={BarChart3}>
            <p>Every confirmed match updates your stats:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong>K/D Ratio</strong> — overall kills to deaths across all modes</li>
              <li><strong>Matches Played</strong> — total confirmed match nights</li>
              <li><strong>Best Eliminations</strong> — your highest kill count in a single game</li>
              <li><strong>Per-mode breakdown</strong> — separate stats for S&D, Hardpoint, and Overload</li>
            </ul>
            <p className="mt-2">Stats are tracked per-competition and as lifetime aggregates.</p>
          </Collapsible>

          <Collapsible title="Your Player Card" icon={UserCheck}>
            <p>Every player gets a card showing their stats, clan, and personal branding. You can customise yours with:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>A personal avatar image</li>
              <li>A banner image</li>
              <li>A card colour</li>
            </ul>
            <p className="mt-2">Your card can be rendered as a 1200x630 PNG — perfect for sharing on socials, Discord, or Twitter.</p>
          </Collapsible>
        </div>
      </Card>
      </div>

      {/* ═══ FOR ORGANISATIONS — only show if admin+ ═══ */}
      <div className={SETTLE} style={settleDelay(10)}>
      <Card>
        <h2 className="font-display text-lg font-800 uppercase tracking-wider text-ink-primary mb-4">For Tournament Organisers</h2>
        <p className="text-sm mb-4 text-ink-secondary">Running a league? Here's how to get set up with GG Anvil.</p>

        <div className="space-y-3">
          <Collapsible title="Setting Up Your Organisation" icon={Building2}>
            <div className="space-y-4">
              <Step number={1} title="Contact Us">
                <p>Reach out at <strong>hello@goodgameanvil.com</strong> or through the contact form on goodgameanvil.com. We'll get your org set up — usually within 24 hours.</p>
              </Step>
              <Step number={2} title="Add the Bot to Your Server">
                <p>We'll send you an invite link for the GG Anvil Discord bot. Add it to your server, give it the necessary permissions (send messages, manage roles, use slash commands), and you're connected.</p>
              </Step>
              <Step number={3} title="Create Your Org">
                <p>Through the portal, create your organisation. Set your name, invite your admin team, and assign roles (Owner, Admin, Member, Viewer).</p>
              </Step>
              <Step number={4} title="Set Up Branding">
                <p>Upload your org logo, banner, and any clan logos to the asset manager. These show up across the portal, match cards, and player profiles.</p>
              </Step>
              <Step number={5} title="Launch a Competition">
                <p>Create a competition: set the team size and game modes, and compose your stage pipeline (Seeding, Round Robin, Divisions, Swiss, Elimination — in any order).</p>
              </Step>
              <Step number={6} title="Enrol Clans">
                <p>Import clans and their rosters. You can bulk-import from a spreadsheet, or have leaders create clans through Discord commands. Ghost seats let you populate full rosters before players even join the platform.</p>
              </Step>
              <Step number={7} title="Let It Run">
                <p>Matches go live on schedule. The bot DMs leaders, players submit screenshots, Anvil's AI reads the scores and reaches consensus, standings update. You handle disputes and edge cases — Anvil handles the rest.</p>
              </Step>
            </div>
            <p className="mt-4">Everything above lives under one <strong>Setup</strong> dashboard with a go-live checklist — org linked, competition created, stages composed, clans enrolled, activated, rules reviewed, public page published.</p>
          </Collapsible>

          <Collapsible title="Company & Brand Integration" icon={Globe}>
            <p>GG Anvil is built for white-label integration:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li><strong>Custom branding</strong> — your org's logos and colours throughout the portal experience</li>
              <li><strong>Discord-native</strong> — runs entirely through your existing Discord server. No separate app for players to download.</li>
              <li><strong>Public pages</strong> — orgs and competitions can each publish a branded public page (<code className="rounded bg-iron-800 px-1 py-0.5 font-mono text-[10px]">/org/…</code>, <code className="rounded bg-iron-800 px-1 py-0.5 font-mono text-[10px]">/comp/…</code>), plus a no-login rules page and an opt-in spectator page — all authored from Setup</li>
              <li><strong>API-driven</strong> — all portal data is accessible via API, so you can build custom dashboards, stream overlays, or embed standings on your website</li>
              <li><strong>Scalable</strong> — from 4-team local scrims to 50+ team national leagues. Same platform, same workflow.</li>
              <li><strong>Simple pricing</strong> — pay-per-read credits plus a small flat monthly org fee, in USD. Built for the South African esports scene first, expanding globally</li>
            </ul>
          </Collapsible>

          <Collapsible title="Billing" icon={CreditCard}>
            <p>No seat licences and no plan tiers. Just two lines on the bill:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong>Credits</strong> — 1&cent; each, one per screenshot the AI reads. Bought in bundles ($10 / 1,000 · $45 / 5,000 · $200 / 25,000) and managed at the org level. They never expire.</li>
              <li><strong>$5 / month per organisation</strong> — a flat data fee. That's it.</li>
            </ul>
            <p className="mt-2">You're only charged a credit when the OCR actually reads an image, and duplicate submissions are locked before they cost anything. This is launch pricing and can change with notice — early orgs won't be repriced without warning.</p>
          </Collapsible>
        </div>
      </Card>
      </div>

      {/* ═══ FAQ ═══ */}
      <div className={SETTLE} style={settleDelay(11)}>
      <Card>
        <h2 className="font-display text-lg font-800 uppercase tracking-wider text-ink-primary mb-4">FAQ</h2>
        <div className="space-y-3">
          <Collapsible title="My portal link expired — what do I do?" icon={AlertCircle}>
            <p>Just run <code className="rounded bg-iron-800 px-1.5 py-0.5 font-mono text-xs">/ggportal</code> again in Discord. Links have different lifetimes depending on your role (founders get longer sessions). You'll need to re-authenticate periodically for security.</p>
          </Collapsible>

          <Collapsible title="The OCR read my screenshot wrong — now what?" icon={AlertCircle}>
            <p>Leaders can flag an incorrect result, and an org admin can manually override scores through the admin panel — that's the path for any dispute. The consensus check catches most bad reads before they land.</p>
          </Collapsible>

          <Collapsible title="Can I be in multiple clans?" icon={AlertCircle}>
            <p>You can only have one active seat per competition. You can be in different clans across different competitions, but not two clans in the same comp.</p>
          </Collapsible>

          <Collapsible title="How do I change my gamertag?" icon={AlertCircle}>
            <p>Edit it through your player card in the portal (<strong>My Card</strong>) — that's the main way to update it now. The old <code className="rounded bg-iron-800 px-1.5 py-0.5 font-mono text-xs">/user_profile_update</code> Discord command is no longer a live global command. Note: changing your gamertag may trigger an integrity flag for admins to review.</p>
          </Collapsible>

          <Collapsible title="What happens if a player doesn't submit?" icon={AlertCircle}>
            <p>There's a submission deadline (usually 24h). If a player misses it, the match can be resolved by the admin through a manual override. Repeated no-shows are visible in the activity log.</p>
          </Collapsible>
        </div>
      </Card>
      </div>
    </div>
  );
}
